/*
 * Which UI language a visit gets, before anyone chose one: English, Spanish or Brazilian Portuguese, the languages the app offers.
 * A visitor from anywhere else (Germany, Poland and Portugal included) gets English.
 */

export const AUTO_LANGUAGES = ['en', 'es', 'pt-BR'] as const;

const SPANISH_COUNTRIES = [
  'ES', 'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'GT', 'CU', 'BO', 'DO', 'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY', 'PR', 'GQ',
];

/** The language for a Cloudflare CF-IPCountry code (via nginx's geo_country cookie). */
export function languageFromCountry(country: string | undefined): string | undefined {
  if (!country) return undefined;
  if (country === 'BR') return 'pt-BR';
  // Spain and Spanish-speaking Latin America: neutral Latin American copy.
  if (SPANISH_COUNTRIES.includes(country)) return 'es';
  // Everyone else, Portugal, Germany, Austria, Switzerland and Poland included.
  return 'en';
}

/**
 * The first browser language that maps to an automatic language ("es-MX" is Spanish, "pt-BR" and a bare
 * "pt" are Brazilian Portuguese, "en-GB" is English). "de", "pl" and "pt-PT" are skipped; i18next falls back
 * to English when nothing matches.
 */
export function languageFromBrowser(codes: readonly string[]): string | undefined {
  // A Portuguese browser sends ["pt-PT", "pt"]: the bare "pt" after a regional pt-* tag is not Brazilian.
  let regionalPt = false;
  for (const code of codes) {
    const lower = code.toLowerCase();
    const base = lower.split('-')[0];
    if (base === 'en') return 'en';
    if (base === 'es') return 'es';
    if (lower === 'pt-br' || (lower === 'pt' && !regionalPt)) return 'pt-BR';
    if (base === 'pt') regionalPt = true;
  }
  return undefined;
}

export const LANGUAGE_STORAGE_KEY = 'cleancal_language';

/**
 * German, Polish and European Portuguese were removed (owner decision, 2026-10-05). A browser that stored one of them, as a choice or
 * as an old automatic pick, has the stored value dropped on every start so detection runs again (English unless the country or the
 * browser says Spanish or Brazilian Portuguese).
 */
export function migrateStoredLanguage(storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined): void {
  if (!storage) return;
  try {
    const stored = storage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && !(AUTO_LANGUAGES as readonly string[]).includes(stored)) storage.removeItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // storage blocked (private mode): nothing stored, nothing to drop
  }
}
